import { useMemo, useState } from 'react'
import { TrendingUp, Users, Package, AlertTriangle, Clock, UserX, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * Tone palette mirrored from KpiCard's ACCENT_STYLES semantic meanings (red =
 * overdue/alert, amber = pending/warning, green = revenue/positive, terracotta
 * = neutral/info) so these cards read as part of the same visual language
 * instead of introducing a new one.
 */
const TONE_STYLES: Record<Tone, { bg: string; border: string; icon: string; text: string }> = {
  alert: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', text: 'text-red-800' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', text: 'text-amber-800' },
  positive: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', text: 'text-green-800' },
  info: { bg: 'bg-terracotta-50', border: 'border-terracotta-200', icon: 'text-terracotta-600', text: 'text-terracotta-800' },
}

type Tone = 'alert' | 'warning' | 'positive' | 'info'

interface Insight {
  id: string
  tone: Tone
  icon: LucideIcon
  message: string
}

interface Sale {
  sale_date: string
  total_amount: number | string
  customer_name?: string | null
  variant_name?: string | null
  quantity?: number
}

interface CollectionsSummary {
  summary: { total_pending: number; total_overdue: number; customers_with_debt: number }
}

interface InactiveCustomer {
  name: string
  days_inactive: number | null
}

interface SellerInsightCardsProps {
  sales: Sale[]
  collections: CollectionsSummary | null
  inactiveCustomers: InactiveCustomer[]
  neverPurchasedCount: number
}

const MAX_INSIGHTS = 3

function startOfWeek(d: Date): Date {
  const date = new Date(d)
  const day = date.getDay() // 0 = Sunday
  date.setDate(date.getDate() - day)
  date.setHours(0, 0, 0, 0)
  return date
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

/**
 * Builds the ranked list of insight candidates from data the seller
 * dashboard already fetches (no new network calls). Deliberately computes
 * its own fixed calendar week/month windows here instead of reusing the
 * dashboard's own useMemo values, which are coupled to whatever date-range
 * filter the user currently has selected on the charts below -- insights
 * must stay stable regardless of that filter.
 */
function buildInsights(
  sales: Sale[],
  collections: CollectionsSummary | null,
  inactiveCustomers: InactiveCustomer[],
  neverPurchasedCount: number
): Insight[] {
  const now = new Date()
  const weekStart = startOfWeek(now)
  const monthStart = startOfMonth(now)

  const salesThisWeek = sales.filter((s) => new Date(s.sale_date) >= weekStart)
  const salesThisMonth = sales.filter((s) => new Date(s.sale_date) >= monthStart)

  const candidates: (Insight | null)[] = []

  const totalOverdue = collections?.summary.total_overdue || 0
  candidates.push(
    totalOverdue > 0
      ? {
          id: 'overdue_collections',
          tone: 'alert',
          icon: AlertTriangle,
          message: `Tienes S/ ${totalOverdue.toFixed(2)} en cobros vencidos de ${collections?.summary.customers_with_debt || 0} cliente(s). Revisa tu cartera de cobranza.`,
        }
      : null
  )

  const longInactive = inactiveCustomers.filter((c) => c.days_inactive !== null && c.days_inactive >= 30)
  candidates.push(
    longInactive.length > 0
      ? {
          id: 'inactive_customers',
          tone: 'warning',
          icon: Clock,
          message: `${longInactive.length} cliente(s) llevan más de 30 días sin comprarte.`,
        }
      : null
  )

  const weekTotal = salesThisWeek.reduce((sum, s) => sum + Number(s.total_amount || 0), 0)
  candidates.push(
    weekTotal > 0
      ? {
          id: 'week_sales',
          tone: 'positive',
          icon: TrendingUp,
          message: `Esta semana vendiste S/ ${weekTotal.toFixed(2)} en ${salesThisWeek.length} venta(s).`,
        }
      : null
  )

  const clientTotals = new Map<string, number>()
  salesThisMonth.forEach((s) => {
    const name = s.customer_name || 'Desconocido'
    clientTotals.set(name, (clientTotals.get(name) || 0) + Number(s.total_amount || 0))
  })
  const topClient = Array.from(clientTotals.entries()).sort((a, b) => b[1] - a[1])[0]
  candidates.push(
    topClient
      ? {
          id: 'top_client_month',
          tone: 'positive',
          icon: Users,
          message: `Tu mejor cliente este mes es ${topClient[0]} con S/ ${topClient[1].toFixed(2)}.`,
        }
      : null
  )

  const productTotals = new Map<string, number>()
  salesThisMonth.forEach((s) => {
    const name = s.variant_name || 'Desconocido'
    productTotals.set(name, (productTotals.get(name) || 0) + (s.quantity || 0))
  })
  const topProduct = Array.from(productTotals.entries()).sort((a, b) => b[1] - a[1])[0]
  candidates.push(
    topProduct
      ? {
          id: 'top_product_month',
          tone: 'info',
          icon: Package,
          message: `Tu producto más vendido este mes es ${topProduct[0]} con ${topProduct[1]} unidad(es).`,
        }
      : null
  )

  candidates.push(
    neverPurchasedCount > 0
      ? {
          id: 'never_purchased',
          tone: 'info',
          icon: UserX,
          message: `Tienes ${neverPurchasedCount} cliente(s) registrados que nunca te han comprado -- ¿los contactamos?`,
        }
      : null
  )

  return candidates.filter((c): c is Insight => c !== null).slice(0, MAX_INSIGHTS)
}

/** Dismissible "did you know" cards about the seller's own sales, shown once
 * per fresh login (the parent controls whether this renders at all via the
 * sessionStorage 'just_logged_in' flag -- see DashboardPage.tsx). Closing a
 * card only removes it from this component's local state; nothing is
 * persisted, since the whole point is that it comes back next login. */
export default function SellerInsightCards({
  sales,
  collections,
  inactiveCustomers,
  neverPurchasedCount,
}: SellerInsightCardsProps) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())

  const insights = useMemo(
    () => buildInsights(sales, collections, inactiveCustomers, neverPurchasedCount),
    [sales, collections, inactiveCustomers, neverPurchasedCount]
  )

  const visibleInsights = insights.filter((i) => !dismissedIds.has(i.id))

  if (visibleInsights.length === 0) return null

  const dismiss = (id: string) => {
    setDismissedIds((prev) => new Set(prev).add(id))
  }

  return (
    <div className="mb-6 space-y-2">
      {visibleInsights.map((insight) => {
        const styles = TONE_STYLES[insight.tone]
        const Icon = insight.icon
        return (
          <div
            key={insight.id}
            role="status"
            className={`flex items-start gap-3 rounded-lg border p-3 ${styles.bg} ${styles.border}`}
          >
            <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${styles.icon}`} aria-hidden="true" />
            <p className={`flex-1 text-sm ${styles.text}`}>{insight.message}</p>
            <button
              type="button"
              onClick={() => dismiss(insight.id)}
              aria-label="Cerrar"
              className={`shrink-0 rounded p-0.5 hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${styles.icon}`}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
